/* Play Stores — bespoke behaviour layered over Ella.
 *
 * Same rules as custom-playstores.css: nothing here edits a vendor file, and
 * everything is scoped to the sections that asked for it.
 */

/* ---------------------------------------------------------------------------
 * 1. Accordion groups: opening one row closes the others
 *
 * Ella has no concept of an accordion GROUP. Every row is its own
 * `<accordion-custom>` element (blocks/_accordion-row.liquid) wrapping its own
 * <details>, and nothing coordinates siblings -- so every row a visitor opens
 * stays open and the list just grows.
 *
 * Closing is delegated to Ella's own `transition(false)` rather than stripping
 * the `open` attribute, so the row animates shut exactly like a click on it
 * would. Yanking the attribute would also leave behind the inline height that
 * `transition()` writes, and the row would collapse to a wrong size.
 *
 * Not done with `<details name="...">`, the native exclusive-accordion feature,
 * for the same reason: the browser would force siblings shut instantly, skipping
 * Ella's animation and leaving that stale inline height in place.
 *
 * Listener is on the document rather than per row: the rows are theme blocks that
 * the client can add, remove and reorder in the editor, and a document listener
 * keeps working without anything to re-bind. It runs after Ella's own summary
 * handler, so by the time it sees the click the row's state is already flipped.
 * ------------------------------------------------------------------------- */

const PLAY_ACCORDION_GROUPS = [
  // Containers whose rows should behave as one group -- opening one closes the
  // others. Full selectors rather than section-key suffixes, so a group can be a
  // section OR a block: the product page's accordion is a block, not a section, and
  // there is no id to match on.
  "[id$='__section_faq']",
  // Every instance of Ella's accordion block, via the class its schema sets. Scoped
  // by TYPE, so a second accordion anywhere gets the behaviour with no edit here.
  // Deliberately not `accordion-custom` on its own: the footer's mobile menus are
  // built from the same element and are independent of each other by design.
  '.accordion',
];

function playAccordionGroupSelector() {
  return PLAY_ACCORDION_GROUPS.join(',');
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('summary');
  if (!summary) return;

  const group = summary.closest(playAccordionGroupSelector());
  if (!group) return;

  const row = summary.closest('accordion-custom');
  const details = summary.closest('details');
  if (!row || !details) return;

  // Ella flips the state synchronously on open, so an unopened row here means the
  // visitor is closing this one -- and closing a row shouldn't disturb the rest.
  if (!details.hasAttribute('open')) return;

  group.querySelectorAll('accordion-custom').forEach((other) => {
    if (other === row) return;

    const otherDetails = other.querySelector('details');
    if (!otherDetails || !otherDetails.hasAttribute('open')) return;

    if (typeof other.transition === 'function') {
      other.transition(false);
    } else {
      // The custom element hasn't upgraded yet. Better a jump than a row that
      // won't close at all.
      otherDetails.removeAttribute('open');
    }
  });
});

/* ---------------------------------------------------------------------------
 * 2. Store open/closed status
 *
 * Evaluates the schedule published by blocks/play-store-status.liquid and rewrites
 * the visible text.
 *
 * Everything hinges on comparing "now" against the schedule IN THE STORE'S
 * TIMEZONE. `Intl.DateTimeFormat` with an explicit IANA zone is what makes that
 * correct, and it handles DST for free -- a fixed UTC offset would be an hour wrong
 * for half the year. The visitor's own clock is irrelevant: whether a Kelowna shop is
 * open depends on the time in Kelowna.
 *
 * Runs in the browser rather than Liquid because pages come from Shopify's full-page
 * CDN cache, so anything server-rendered could be served hours stale.
 * ------------------------------------------------------------------------- */

const PLAY_DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

// Wall-clock time at the store, as {y, m, d, weekday, minutes}. Formatting to parts
// in the target zone and reading them back is the only way to get a zone's local
// calendar date without pulling in a date library.
function playStoreNow(timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date());

  const get = (type) => (parts.find((p) => p.type === type) || {}).value;

  // 'en-CA' gives hour 00-24; 24 shows up at exactly midnight in some engines.
  const hour = parseInt(get('hour'), 10) % 24;
  const minute = parseInt(get('minute'), 10);

  const short = (get('weekday') || '').toLowerCase().slice(0, 3);
  const weekday = PLAY_DAY_NAMES.findIndex((d) => d.startsWith(short));

  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    weekday: weekday === -1 ? new Date().getDay() : weekday,
    minutes: hour * 60 + minute,
  };
}

// Minutes after midnight, or null. The metaobject asks for 24-hour "HH:MM", but
// the field is typed by a person, so "10am", "6:30 pm" and "18.00" are accepted
// too. A value that still fails to parse must not read as "Closed" on a day the
// shop is open, which is what a strict parser did.
function playParseTime(value) {
  if (!value) return null;
  const match = String(value)
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?:[:.](\d{2}))?\s*([ap])?\.?\s*m?\.?$/);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const mins = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3];

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === 'p' ? 12 : 0);
  } else if (!match[2]) {
    // A bare number with no minutes and no am/pm ("10") is too ambiguous.
    return null;
  }

  if (hours > 23 || mins > 59) return null;
  return hours * 60 + mins;
}

// 14:00 -> "2 pm", 18:30 -> "6:30 pm". Minutes are dropped when zero, which is how
// the wireframe writes it.
function playFormatTime(minutes) {
  const hour24 = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const suffix = hour24 < 12 ? 'am' : 'pm';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return mins === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

// The schedule for a given weekday offset from today, exceptions taking precedence.
function playDayFor(schedule, now, offset) {
  const weekday = (now.weekday + offset) % 7;
  const name = PLAY_DAY_NAMES[weekday];

  // Offset the calendar date too, so an exception is matched against the right day.
  const base = new Date(`${now.date}T00:00:00`);
  base.setDate(base.getDate() + offset);
  const iso = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}`;

  const exception = (schedule.exceptions || []).find((e) => e.date === iso);
  const day = exception || (schedule.week || {})[name] || null;

  return day ? { ...day, name, iso, isException: Boolean(exception) } : null;
}

function playStatusFor(schedule, now) {
  const today = playDayFor(schedule, now, 0);
  const soon = Number(schedule.closingSoonMinutes) || 0;

  if (today && !today.closed) {
    const opens = playParseTime(today.opens);
    const closes = playParseTime(today.closes);

    if (opens !== null && closes !== null) {
      if (now.minutes < opens) {
        return { state: 'closed', text: `Closed · Opens at ${playFormatTime(opens)}` };
      }
      if (now.minutes < closes) {
        const left = closes - now.minutes;
        if (soon > 0 && left <= soon) {
          return { state: 'closing-soon', text: `Closing in ${left} min · ${playFormatTime(closes)}` };
        }
        return { state: 'open', text: `Open · Closes at ${playFormatTime(closes)}` };
      }
      // Past closing: fall through and look for the next open day.
    } else {
      // Open today, but the times will not parse. Saying nothing definite beats
      // falling through to "Closed" on a day the shop is open; the week list
      // below still shows what was entered.
      return { state: 'unknown', text: 'Store hours' };
    }
  }

  // Next open day within a week. Stops at 7 rather than looping forever if every day
  // is marked closed.
  for (let offset = 1; offset <= 7; offset += 1) {
    const day = playDayFor(schedule, now, offset);
    if (!day || day.closed) continue;
    const opens = playParseTime(day.opens);
    if (opens === null) continue;
    const when = offset === 1
      ? 'tomorrow'
      : day.name.charAt(0).toUpperCase() + day.name.slice(1);
    const reason = today && today.closed && today.isException && today.label ? `${today.label} · ` : '';
    return { state: 'closed', text: `${reason}Closed · Opens ${when} at ${playFormatTime(opens)}` };
  }

  return { state: 'closed', text: 'Closed' };
}

class PlayStoreStatus extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-play-hours]');
    this.target = this.querySelector('[data-play-status]');
    this.toggle = this.querySelector('[data-play-toggle]');
    this.panel = this.querySelector('[data-play-schedule]');
    if (!data || !this.target || !this.toggle || !this.panel) return;

    let schedule;
    try {
      schedule = JSON.parse(data.textContent);
    } catch (error) {
      // Leave the full schedule visible rather than show a broken status.
      return;
    }
    if (!schedule || !schedule.week) return;
    this.schedule = schedule;

    // Drop exceptions that have already passed. Liquid can't do this: "future" would
    // be evaluated when the page was cached, not when it is read.
    this.pruneExceptions();

    // Only now take over: reveal the toggle and collapse the list.
    this.toggle.hidden = false;
    this.panel.hidden = true;
    this.toggle.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);

    this.render();
    this.timer = window.setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    if (this.timer) window.clearInterval(this.timer);
  }

  pruneExceptions() {
    let today;
    try {
      today = playStoreNow(this.schedule.timezone || 'America/Vancouver').date;
    } catch (error) {
      return;
    }
    this.querySelectorAll('[data-exception-date]').forEach((row) => {
      row.hidden = row.dataset.exceptionDate < today;
    });
  }

  onToggle = () => {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    this.panel.hidden = open;
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.toggle.getAttribute('aria-expanded') !== 'true') return;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    this.toggle.focus();
  };

  render() {
    let now;
    try {
      now = playStoreNow(this.schedule.timezone || 'America/Vancouver');
    } catch (error) {
      // An invalid IANA name throws. Keep the plain schedule rather than guess.
      return;
    }

    const status = playStatusFor(this.schedule, now);
    this.dataset.state = status.state;
    this.target.textContent = status.text;

    // Mark today in the dropdown so the row being described is obvious, and restate
    // the times through the same formatter as the status line -- Liquid renders the raw
    // "10:00-18:00" from the metaobject, which reads inconsistently next to "6 pm".
    const todayName = PLAY_DAY_NAMES[now.weekday];
    this.querySelectorAll('[data-day]').forEach((row) => {
      row.classList.toggle('is-today', row.dataset.day === todayName);

      const day = (this.schedule.week || {})[row.dataset.day];
      const cell = row.querySelector('.play-status__time');
      if (!day || !cell) return;

      if (day.closed) {
        cell.textContent = 'Closed';
        return;
      }
      const opens = playParseTime(day.opens);
      const closes = playParseTime(day.closes);
      if (opens === null || closes === null) return;
      cell.textContent = `${playFormatTime(opens)} \u2013 ${playFormatTime(closes)}`;
    });
  }
}

if (!customElements.get('play-store-status')) {
  customElements.define('play-store-status', PlayStoreStatus);
}
